import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Search, X, User, Check } from 'lucide-react';

export default function GuestSelector({ guests, value, onChange, placeholder = "Rechercher un client..." }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filtrer les clients
  const filteredGuests = guests.filter(guest => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const fullName = `${guest.first_name} ${guest.last_name}`.toLowerCase();
    return fullName.includes(searchLower) || 
           guest.email?.toLowerCase().includes(searchLower) ||
           guest.phone?.includes(search);
  });

  // Client sélectionné
  const selectedGuest = guests.find(g => String(g.id) === String(value));

  // Gérer la sélection
  const handleSelect = (guestId) => {
    onChange(String(guestId));
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(0);
  };

  // Gérer les touches clavier
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, filteredGuests.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredGuests[highlightedIndex]) {
          handleSelect(filteredGuests[highlightedIndex].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearch('');
        break;
    }
  };

  // Fermer en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset index quand la recherche change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  return (
    <div ref={containerRef} className="relative">
      {/* Bouton principal */}
      <div 
        className={`flex items-center w-full px-3 py-2 border rounded-md cursor-pointer transition-colors ${isOpen ? 'border-primary ring-2 ring-primary/20' : 'border-input hover:bg-muted/50'}`}
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        data-testid="guest-selector"
      >
        <User className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
        {selectedGuest ? (
          <div className="flex items-center justify-between flex-1 min-w-0">
            <span className="truncate">{selectedGuest.first_name} {selectedGuest.last_name}</span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 ml-2 shrink-0"
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-[300px] overflow-hidden">
          {/* Recherche */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tapez pour rechercher..."
                className="pl-8 h-9"
                autoFocus
              />
            </div>
          </div>
          
          {/* Liste */}
          <div className="max-h-[220px] overflow-y-auto">
            {filteredGuests.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                Aucun client trouvé
              </div>
            ) : (
              filteredGuests.map((guest, index) => (
                <div
                  key={guest.id}
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${
                    index === highlightedIndex ? 'bg-primary/10' : 'hover:bg-muted'
                  } ${String(guest.id) === String(value) ? 'bg-primary/5' : ''}`}
                  onClick={() => handleSelect(guest.id)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{guest.first_name} {guest.last_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {guest.email || guest.phone || 'Pas de contact'}
                      </p>
                    </div>
                  </div>
                  {String(guest.id) === String(value) && (
                    <Check className="w-4 h-4 text-primary shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>
          
          {/* Info */}
          <div className="p-2 border-t bg-muted/30 text-xs text-muted-foreground">
            ↑↓ pour naviguer • Entrée pour sélectionner • Échap pour fermer
          </div>
        </div>
      )}
    </div>
  );
}
